import { useEffect, useState } from 'react'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectActiveMatterExternalSyncMapping,
  selectMatterExternalSyncMappingRequest,
} from '@/user_files/external-storage/store/slice'

type DriveConnectivity = {
  provider: ExternalStorageProvider
  status: 'connected' | 'disconnected'
  connectedByUserId: string
  syncedFolderPath: string | null
}

/** Account health for this matter's mapped folder, after fresh page-entry reads succeed. */
export function useMatterDriveConnectivity(matterId: string | undefined, enabled: boolean): DriveConnectivity | null {
  const dispatch = useAppDispatch()
  const mapping = useAppSelector((state) => selectActiveMatterExternalSyncMapping(state, matterId))
  const mappingRequest = useAppSelector((state) => selectMatterExternalSyncMappingRequest(state, matterId))
  const [refreshedMatterId, setRefreshedMatterId] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !matterId) return

    let active = true
    setRefreshedMatterId(null)
    void dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId })).then(() => {
      if (active) setRefreshedMatterId(matterId)
    })

    return () => {
      active = false
    }
  }, [dispatch, enabled, matterId])

  if (!enabled || !matterId || refreshedMatterId !== matterId || mappingRequest.status !== kExternalStorageRequestStatus.SUCCESS || !mapping) {
    return null
  }

  const connectivity = {
    provider: mapping.provider,
    connectedByUserId: mapping.connected_by_user_id,
    syncedFolderPath: mapping.external_folder_path ?? mapping.external_folder_name,
  }

  if (mapping.account_status === 'connected') return { ...connectivity, status: 'connected' }
  if (mapping.account_status === 'error' || mapping.account_status === 'reauth_required') {
    return { ...connectivity, status: 'disconnected' }
  }
  return null
}
